import React, { Dispatch } from 'react';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import reducer, { Action, State } from './reducers/map-reducer';
import { AuthorConfig } from '../models/map/author-config';
import {
  DEFAULT_BOUNDS,
  DEFAULT_CENTER_COORDINATES,
  DEFAULT_ZOOM,
} from '../components/map/Map/MapUtils';
import { getRadiusOptions } from '../utils/authorConfigUtils';

export const defaultState: State = {
  query: '',
  marketGoogleKey: '',
  devMode: false,
  locale: {
    countryCode: '',
    language: '',
  },
  intlSearchEnabled: true,
  baseUrl: '',
  results: null,
  dealerRatings: {},
  dealerReviews: null,
  ratingSummary: null,
  reviewCategoryFilter: 'ALL',
  reviewStarFilter: 'All stars',
  reviewSort: 'Newest',
  isFlyoutOpen: false,
  hoveredDealer: null,
  isGoogleMapsApiLoaded: false,
  currentResultsAvailableFilters: [],
  userSelectedFilters: [],
  authorDefinedFilters: [],
  preselectedFilters: [],
  isSidebarOpen: true,
  isAutocompletionOpen: false,
  isFiltersOverlayOpen: false,
  searchText: '',
  showAddress: false,
  searchRadius: -1,
  searchRadiusOptions: [10],
  initialMapZoomLevel: '',
  isLoading: false,
  searchError: null,
  searchGeoLocation: {
    lat: 0,
    lng: 0,
  },
  placeId: '',
  northEastPosition: { lat: 0, lng: 0 },
  southWestPosition: { lat: 0, lng: 0 },
  isMapTriggeredByTeaser: false,
  isUserLocationError: false,
  isUserLocationPermissionGiven: false,
  showLocationError: false,
  updateMapZoomLevel: true,
  selectedDealerId: null,
  showRadius: true,
  searchRadiusExtended: false,
  isUserPositionUsed: false,
  groupMarkers: false,
  activeMobileView: 'map',
  lastSubmittedSearch: null,
  mapZoom: DEFAULT_ZOOM,
  searchInThisAreaVisible: false,
  bounds: DEFAULT_BOUNDS,
  mapCenterCoordinates: DEFAULT_CENTER_COORDINATES,
  userGeolocation: null,
  distanceUnit: 'km',
  showPartnerSuggestions: true,
  ctaBarButtonConfig: [],
  displayCtaBar: false,
  showOpeningHours: true,
  pbcVersion: '',
  twoClickConsentRequired: false,
  twoClickData: {
    headline: '',
    copyText: '',
    toggleLabel: '',
    asideToggleHeadline: '',
    asideToggleLabel: '',
    asideToggleCopy: '',
    closeButton: {
      text: '',
      redirectURL: '',
    },
  },
  fiveStarData: {
    displayFiveStarRating: false,
    legalText: '',
  },
  favoritePartner: {
    kvpsid: '',
    tenant: '',
  },
  myAudiCtaConfig: null,
  accessToken: null,
  selectedPartner: {
    kvpsid: '',
    tenant: '',
  },
  errorMessage: {
    code: '',
    message: '',
  },
  showModal: false,
  oneHeader: false,
  dealerContextService: undefined,
  connectedDealer: null,
  connectDealer: false,
  autoConnectDealer: false,
  showComments: true,
};
export const mapStateContext = React.createContext<State | null>(null);
export const mapDispatchContext = React.createContext<Dispatch<Action> | null>(null);

const Provider = ({
  children,
  query,
  isMapTriggeredByTeaser,
  authorConfig,
  baseUrl,
  filters,
  searchGeoLocation,
  selectedDealerId,
  locale,
  accessToken,
  selectedPartner,
  favoritePartner,
  dealerContextService,
  connectedDealer,
}: {
  children: React.ReactNode;
  query: string;
  isMapTriggeredByTeaser: boolean;
  authorConfig: AuthorConfig;
  baseUrl: string;
  filters: string[];
  searchGeoLocation: Coordinates;
  selectedDealerId: string | null;
  locale: LocaleServiceConfig;
  accessToken?: string | null | undefined;
  selectedPartner: {
    kvpsid: string;
    tenant: string;
  };
  favoritePartner: {
    kvpsid: string;
    tenant: string;
  };
  dealerContextService: undefined | DealerContextServiceV1;
  connectedDealer: string | null;
}) => {
  const [initSearchRadius, searchRadiusOptions] = getRadiusOptions(authorConfig);

  const [state, dispatch] = React.useReducer(reducer, {
    ...defaultState,
    query,
    searchText: query,
    authorDefinedFilters: authorConfig.filters,
    preselectedFilters: authorConfig.preselectedFilters,
    userSelectedFilters: [...filters],
    locale: {
      countryCode: locale?.countryCode ?? '',
      language: locale?.language ?? '',
    },
    searchGeoLocation,
    isMapTriggeredByTeaser,
    selectedDealerId,
    baseUrl,
    marketGoogleKey: authorConfig.marketGoogleKey,
    devMode: authorConfig.devMode,
    initialMapZoomLevel: authorConfig.initialMapZoomLevel,
    searchRadiusOptions,
    searchRadius: initSearchRadius,
    showAddress: authorConfig.showAddress,
    groupMarkers: authorConfig.groupMarkers,
    intlSearchEnabled: authorConfig.intlSearchEnabled,
    distanceUnit: authorConfig.distanceUnit,
    showPartnerSuggestions: authorConfig.showPartnerSuggestions,
    ctaBarButtonConfig: authorConfig.ctaBarButtonConfig,
    displayCtaBar: authorConfig.displayCtaBar,
    showOpeningHours: authorConfig.showOpeningHours,
    pbcVersion: authorConfig.pbcVersion || '',
    twoClickConsentRequired: authorConfig.twoClickConsentRequired || false,
    twoClickData: authorConfig.twoClick || {},
    fiveStarData: authorConfig.fiveStar || {},
    accessToken,
    selectedPartner,
    favoritePartner,
    myAudiCtaConfig: authorConfig.myAudiCtaConfig,
    oneHeader: authorConfig.oneHeader,
    dealerContextService,
    connectedDealer,
    connectDealer: authorConfig.connectDealer,
    autoConnectDealer: authorConfig.autoConnectDealer,
    showComments: authorConfig.showComments,
  });

  return (
    <mapStateContext.Provider value={state}>
      <mapDispatchContext.Provider value={dispatch}>{children}</mapDispatchContext.Provider>
    </mapStateContext.Provider>
  );
};

function useMapState() {
  const context = React.useContext(mapStateContext);
  if (!context) {
    throw new Error('useMapState must be used within a Provider');
  }
  return context;
}

function useMapDispatch() {
  const context = React.useContext(mapDispatchContext);
  if (!context) {
    throw new Error('useMapDispatch must be used within a Provider');
  }
  return context;
}

export default { Provider, useMapState, useMapDispatch };
